import { useCallback, useEffect, useState } from "react";
import { Icon } from "./Icon";

/**
 * Sahifalash — cursor bo'yicha (dev_spec.md §4.3).
 *
 * Server OFFSET bermaydi, `next_cursor` beradi: o'n minglab qatorli
 * reyestrda `OFFSET 50000` butun jadvalni sanab o'tardi, cursor esa
 * indeksdan boshlab o'qiydi. Buning narxi — oldinga qarab yurish:
 * «oldingi sahifa» uchun cursor yo'q.
 *
 * Shuning uchun ishlatilgan cursorlar STEK sifatida saqlanadi: orqaga
 * qaytish — stekdan bittasini olib tashlash. Aks holda xodim keyingi
 * sahifaga o'tgandan keyin faqat filtrni qayta yozib orqaga
 * qaytolardi.
 */
export function usePager(resetKey: string) {
  // Birinchi element — `undefined`: cursorsiz, ya'ni birinchi sahifa.
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);

  // Filtr yoki qidiruv o'zgarsa birinchi sahifaga qaytiladi: eski
  // cursor boshqa saralashga tegishli bo'lib, tasodifiy qatorlarni
  // ko'rsatardi.
  useEffect(() => {
    setCursors([undefined]);
  }, [resetKey]);

  const next = useCallback((cursor: string | undefined) => {
    if (!cursor) return;
    setCursors((current) => [...current, cursor]);
  }, []);

  const prev = useCallback(() => {
    setCursors((current) => (current.length > 1 ? current.slice(0, -1) : current));
  }, []);

  return {
    /** Shu sahifa uchun so'rov parametri. */
    cursor: cursors[cursors.length - 1],
    /** 1 dan boshlanadigan sahifa raqami. */
    page: cursors.length,
    atFirst: cursors.length === 1,
    next,
    prev,
  };
}

interface PagerProps {
  page: number;
  atFirst: boolean;
  /** Shu sahifada ko'rsatilgan qatorlar soni. */
  shown: number;
  /** Serverda yana qator borligi (`has_more`). */
  hasMore?: boolean;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * Sahifa o'tkazgich — jadval poyasiga qo'yiladi.
 *
 * Jami soni KO'RSATILMAYDI: cursor pagination `COUNT(*)` qilmaydi va
 * uni shu yerda so'rash har sahifada butun jadvalni sanashga olib
 * kelardi. O'rniga sahifa raqami va shu sahifadagi qatorlar soni
 * aytiladi — ikkisi ham haqiqat.
 */
export function Pager({ page, atFirst, shown, hasMore, onPrev, onNext }: PagerProps) {
  return (
    <div className="pager">
      <span className="pager-info">
        {shown > 0 ? `${page}-sahifa · ${shown} qator` : `${page}-sahifa · bo'sh`}
      </span>
      <span className="pager-buttons">
        <button type="button" className="btn btn-outlined btn-sm" onClick={onPrev} disabled={atFirst}>
          <Icon name="chevronLeft" size={14} />
          Oldingi
        </button>
        <button type="button" className="btn btn-outlined btn-sm" onClick={onNext} disabled={!hasMore}>
          Keyingi
          <Icon name="chevronRight" size={14} />
        </button>
      </span>
    </div>
  );
}
